import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsOfService() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-3">
          <FileText className="h-8 w-8 text-primary-500" />
          <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
        </div>
        
        <div className="bg-zinc-900 rounded-lg p-8 mb-8">
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">Last Updated: July 1, 2025</p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">1. Introduction</h2>
            <p className="text-gray-300">
              Welcome to Jacky Circuit. These Terms of Service ("Terms") govern your use of our website, track booking system, event registration platform, and related services (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">2. Definitions</h2>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li><strong>"Jacky Circuit,</strong> refers to the promoter and owner of this page.</li>
              <li><strong>"User</strong> refers to any individual or entity using our Services.</li>
              <li><strong>"Track Booking"</strong> refers to the reservation of track time for drag racing or drift activities.</li>
              <li><strong>"Event Registration"</strong> refers to the process of signing up for racing events hosted at our facility.</li>
              <li><strong>"Security Deposit"</strong> refers to the refundable amount paid to secure certain bookings.</li>
            </ul>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">3. Account Registration</h2>
            <p className="text-gray-300">
              To access certain features of our Services, you must create an account. When you create an account, you agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Be responsible for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p className="text-gray-300 mt-2">
              We reserve the right to suspend or terminate accounts that violate these Terms or for any other reason at our sole discretion.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">4. Track Booking Terms</h2>
            <p className="text-gray-300">
              When booking track time through our Services, you agree to the following:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li><strong>Booking Confirmation:</strong> Track bookings are not confirmed until payment is received and confirmation is issued.</li>
              <li><strong>Cancellation Policy:</strong> Cancellations made more than 48 hours before the booking time will receive a full refund. Cancellations made within 48 hours of the booking time will not be refunded.</li>
              <li><strong>Security Deposits:</strong> For certain bookings (full-day bookings, multi-date bookings, and promotor bookings), a security deposit is required. This deposit is refundable if no damage occurs during your session.</li>
              <li><strong>Lighting Fees:</strong> Additional fees apply for bookings extending beyond 6:00 PM to cover lighting costs.</li>
              <li><strong>Track Rules:</strong> All users must comply with track safety rules and staff instructions.</li>
              <li><strong>Vehicle Requirements:</strong> All vehicles must meet our safety standards. We reserve the right to refuse track access to any vehicle deemed unsafe.</li>
              <li><strong>Weather Conditions:</strong> Jacky Circuit reserves the right to cancel bookings due to adverse weather conditions. In such cases, a full refund or rebooking option will be provided.</li>
            </ul>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">5. Event Registration Terms</h2>
            <p className="text-gray-300">
              When registering for events through our Services, you agree to the following:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li><strong>Registration Confirmation:</strong> Event registrations are not confirmed until payment is received and confirmation is issued.</li>
              <li><strong>Driver Profiles:</strong> Complete and accurate driver profiles are required for event participation.</li>
              <li><strong>Race Numbers:</strong> Race numbers must be unique. Once claimed, a race number cannot be used by another driver.</li>
              <li><strong>Category Rules:</strong> Participants must comply with all rules specific to their registered categories.</li>
              <li><strong>Wristbands and Tickets:</strong> The number of included wristbands and tickets is specified for each category and cannot be exceeded without additional purchase.</li>
              <li><strong>Cancellation Policy:</strong> Event registration fees are non-refundable unless the event is canceled by Jacky Circuit.</li>
            </ul>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">6. Payment Terms</h2>
            <p className="text-gray-300">
              By using our Services, you agree to the following payment terms:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li><strong>Pricing:</strong> All prices are listed in Caribbean Guilders (XCG) and are subject to change without notice.</li>
              <li><strong>Payment Methods:</strong> We accept bank transfers to the accounts specified in booking confirmations.</li>
              <li><strong>Security Deposits:</strong> Security deposits are refundable within 7 business days after your event, provided no damage has occurred.</li>
              <li><strong>Promotor Bookings:</strong> Promotors must complete their profile with valid CRIB and KVK numbers before making bookings.</li>
              <li><strong>Late Payments:</strong> Failure to pay by the specified deadline may result in cancellation of your booking or registration.</li>
            </ul>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">7. User Conduct</h2>
            <p className="text-gray-300">
              When using our Services, you agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon the rights of others</li>
              <li>Submit false or misleading information</li>
              <li>Interfere with the proper functioning of the Services</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use our Services for any illegal or unauthorized purpose</li>
              <li>Harass, threaten, or intimidate other users or staff</li>
            </ul>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">8. Intellectual Property</h2>
            <p className="text-gray-300">
              All content on our website, including text, graphics, logos, images, and software, is the property of Jacky Circuit or our licensors and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any content without our express written permission.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-300">
              To the maximum extent permitted by law, Jacky Circuit shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to, damages for loss of profits, goodwill, use, data, or other intangible losses resulting from:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li>Your use or inability to use our Services</li>
              <li>Any changes made to our Services</li>
              <li>Unauthorized access to or alteration of your data</li>
              <li>Statements or conduct of any third party on our Services</li>
              <li>Any other matter relating to our Services</li>
            </ul>
            <p className="text-gray-300 mt-2">
              Our total liability for any claim arising from or related to these Terms shall not exceed the amount you paid to us in the six months preceding the claim.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">10. Indemnification</h2>
            <p className="text-gray-300">
              You agree to indemnify, defend, and hold harmless Jacky Circuit, its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising from:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li>Your use of our Services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another person or entity</li>
              <li>Your conduct in connection with our Services</li>
            </ul>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">11. Termination</h2>
            <p className="text-gray-300">
              We may terminate or suspend your account and access to our Services immediately, without prior notice or liability, for any reason, including but not limited to a breach of these Terms. Upon termination, your right to use the Services will immediately cease.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">12. Governing Law</h2>
            <p className="text-gray-300">
              These Terms shall be governed by and construed in accordance with the laws of Curaçao, without regard to its conflict of law provisions. Any legal action or proceeding arising out of or relating to these Terms shall be brought exclusively in the courts of Curaçao.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">13. Changes to Terms</h2>
            <p className="text-gray-300">
              We reserve the right to modify these Terms at any time. We will provide notice of significant changes by posting the updated Terms on our website and updating the "Last Updated" date. Your continued use of our Services after such changes constitutes your acceptance of the new Terms.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">14. Contact Information</h2>
            <p className="text-gray-300">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="text-gray-300 mt-2">
              Email: info@jackycircuit.com<br />
              Phone: +1 (555) 123-4567<br />
              Address: Willemhoyerstraat 60, Curaçao
            </p>
          </div>
        </div>
        
        <div className="flex justify-center mb-8">
          <Link to="/" className="flex items-center gap-2 text-primary-500 hover:text-primary-400">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}